import { route } from 'preact-router';
import { useEffect, useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { ResultState } from '../../components/ui/ResultState.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { PAYMENT_NOT_CONFIRMED_YET, goneMessage } from '../../components/ui/resultWording.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useSignInRedirect } from '../../hooks/useSignInRedirect.js';

interface BookingStatus {
  status: string;
}

/** How long to leave between re-reads while waiting for the payment to be recorded. */
export const POLL_INTERVAL_MS = 2000;

/** How many re-reads to take before giving the wait a stated end. */
export const MAX_POLLS = 10;

interface BookingReturnProps {
  id?: string;
  path?: string;
}

export function BookingReturnPage({ id }: BookingReturnProps) {
  const goToSignIn = useSignInRedirect();
  const bookingRead = useFetch<BookingStatus>(id ? `/bookings/${id}` : null);
  const { data: booking, status } = bookingRead;
  const [polls, setPolls] = useState(0);

  useEffect(() => {
    if (!booking) return;
    // 069: `awaiting_payment` is the only status this screen waits on — everything else, from
    // `cancelled` to `checked_out`, means there is nothing left to wait for. They all go to the
    // booking's own screen rather than being restated here: it already renders every status
    // through `StatusBadge`, and a second vocabulary on this screen would drift from it the first
    // time the platform's enum changes.
    if (booking.status !== 'awaiting_payment') {
      route(`/booking/${id}/confirm`);
      return;
    }
    if (polls < MAX_POLLS) {
      // 069: the tick advances the counter AND re-reads, and the pairing is the whole fix.
      //
      // This used to bump the counter alone, which re-rendered the screen and issued nothing —
      // `useFetch` re-runs on `[path, attempt]` and the counter is neither, so ten ticks over
      // twenty seconds took one read. Measured live: a booking already `confirmed` in the
      // database, and the screen still saying "Processing your payment…" at 43 seconds.
      //
      // The counter has to move here, in the tick, and not when the answer lands. `retry()` sets
      // `{status:'loading', data:null}`, so the guard above fires while the read is in flight and
      // this effect arms nothing; the already-advanced counter is the only thing carrying the loop
      // across that gap. Advance it on receipt instead and the budget never moves — the screen
      // polls for ever.
      const timer = setTimeout(() => {
        setPolls((p) => p + 1);
        bookingRead.retry();
      }, POLL_INTERVAL_MS);
      return () => clearTimeout(timer);
    }
  }, [booking, polls, id, bookingRead.retry]);

  // 064: this is where someone lands back from the payment provider. It used to render
  // "Processing your payment…" whatever the read did, so a failed read left a person who had
  // just paid watching a spinner that would never resolve. `idle` still waits — no id has
  // arrived yet, so nothing has been asked.
  if (status !== 'ok' && status !== 'loading' && status !== 'idle') {
    return (
      <div className="page-fill bg-bg flex flex-col items-center justify-center px-4">
        <ResultState
          state={bookingRead}
          onRetry={bookingRead.retry}
          goneMessage={goneMessage('booking')}
          onwardLabel="My bookings"
          onOnward={() => route('/account/bookings')}
          onSignIn={goToSignIn}
        />
      </div>
    );
  }

  // 069: the wait has to end. The budget being spent while the booking is still awaiting payment
  // is derived here rather than stored, so it cannot disagree with what the effect decided — at
  // this point the last read has resolved, so `booking` is held.
  //
  // This is an `ok` read, not a failure, which is why it is not a `ResultState` arm and not red:
  // the platform answered correctly, and the payment may yet come through.
  if (booking?.status === 'awaiting_payment' && polls >= MAX_POLLS) {
    return (
      <div className="page-fill bg-bg flex flex-col items-center justify-center gap-3 px-4">
        <p className="text-text text-center">{PAYMENT_NOT_CONFIRMED_YET}</p>
        <Button variant="ghost" onClick={() => route(`/booking/${id}/confirm`)}>
          View booking
        </Button>
      </div>
    );
  }

  return (
    <div className="page-fill bg-bg flex flex-col px-4">
      {/* 208 (#308): had no back control at all before this feature. */}
      <PageHeader title="Confirm Payment" fallback="/account/bookings" />
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        <Spinner size="lg" />
        <p className="text-text text-center">Processing your payment…</p>
        <p className="text-gray-500 text-sm text-center">You will be redirected once confirmed</p>
      </div>
    </div>
  );
}
